import {all, any} from 'ramda';
import {
  animationFrames,
  combineLatest,
  endWith,
  identity,
  map,
  Observable,
  OperatorFunction,
  takeWhile,
} from 'rxjs';
import {MultipleTargets} from 'tippy.js';
import Decimal, {DecimalSource} from './break_infinity';
import {DynamicCost, DynamicValue} from './interfaces/dynamic-value';
import {ri} from './runner-idle';
import {Vector} from './Vector';

export const findAllDeep = (
  parent: Element,
  selectors: string,
  depth = null
) => {
  const nodes: Set<string | Element | Node> = new Set();
  const currentDepth = 1;
  const recordResult = (nodesArray: MultipleTargets) => {
    for (const node of nodesArray) {
      nodes.add(node);
    }
  };
  const recursiveSeek = (_parent: Element) => {
    // check for selectors in lightdom
    recordResult(_parent.querySelectorAll(selectors));
    if (_parent.shadowRoot) {
      // check for selectors in shadowRoot
      recordResult(_parent.shadowRoot.querySelectorAll(selectors));
      // look for nested elements with shadowRoots
      for (const child of [..._parent.shadowRoot.querySelectorAll('*')].filter(
        (i) => i.shadowRoot
      )) {
        // make sure we haven't hit our depth limit
        if (depth === null || currentDepth < depth) {
          recursiveSeek(child);
        }
      }
    }
  };
  recursiveSeek(parent);
  return nodes;
};

// https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item: unknown): boolean {
  return Boolean(item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep<T extends object>(target: T, ...sources: T[]): T {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(target, {[key]: {}});
        mergeDeep(
          target[key as unknown as keyof T] as unknown as object,
          source[key] as unknown as object
        );
      } else {
        Object.assign(target, {[key]: source[key]});
      }
    }
  }

  return mergeDeep(target, ...sources);
}

export const euclideanDistance: (a: Vector, b: Vector) => Decimal = (a, b) => {
  let dx = a[0].minus(b[0]);
  if (!dx.eq(0)) dx = dx.pow(2);
  let dy = a[1].minus(b[1]);
  if (!dy.eq(0)) dy = dy.pow(2);
  return dx.plus(dy).sqrt();
};

export const formatDistance = (a: Vector, b: Vector) =>
  `${formatEng(euclideanDistance(a, b))}m`;

export const formatPosition: (position: Vector) => string = (position) => {
  return `(${formatEng(position.x)}, ${formatEng(position.y)})`;
};

export const formatCostEffect: (
  item: {
    cost: DynamicCost;
    effect: DynamicCost;
    duration?: number;
    chance?: DynamicValue;
    chanceString?: DynamicValue;
    chanceLabel?: DynamicValue;
  },
  quantity?: number,
  simple?: boolean
) => string = (item, quantity = 1, simple = false) => {
  const totalWeight = [...item.effect].reduce(
    (acc, [, {weight}]) =>
      weight ? new Decimal(weight(-1, 0)).plus(acc) : acc,
    new Decimal(0)
  );
  const newLine = simple ? '' : '<br>';
  return `<span class='costeffect'>${
    item.cost.size > 0
      ? `<span style="font-size: 1rem">Cost:</span><br>${[...item.cost]
          // .filter(([k, v]) => ri.get(k)?.unlocked)
          .map(([k, v]) => {
            const costAmount = new Decimal(v.value(-1, 0)).times(quantity);
            return `<span class='fancy ${k}' data-cost-string>${
              costAmount.lt(0) ? '+' : ''
            }${formatEng(costAmount.abs())}</span> ${ri.get(k)?.displayName}
            ${
              ri.settings.showCostEffectDetails && v.string?.(-1, 0)
                ? `<span class='subtitle'>${v.string?.(-1, 0)}</span>`
                : ''
            }`;
          })
          .join(newLine)} `
      : ''
  } ${
    item.effect.size > 0
      ? `${item.cost.size > 0 ? '<br>' : ''}${
          simple ? '' : '<span style="font-size: 1rem">Effect:</span><br>'
        }${[...item.effect]
          .filter(([k]) => ri.get(k)?.unlocked)
          .filter(([k]) => !ri.get(k)?.tags?.has('hide-effect'))
          .map(
            ([k, v]) =>
              `<span class='fancy ${k}' data-effect-string>${formatEng(
                new Decimal(v.value(-1, 0)).times(quantity)
              )}</span> ${ri.get(k)?.displayName}
            ${
              ri.settings.showCostEffectDetails && v.string?.(-1, 0)
                ? `<span class='subtitle'>${v.string?.(-1, 0)}</span>`
                : ''
            }
            ${
              v.weight
                ? formatPercent(new Decimal(v.weight(-1, 0)).div(totalWeight))
                : ''
            }`
          )
          .join(newLine)}
          `
      : ''
  }${
    simple ? '' : `<br />Length: ${formatTime((item.duration ?? 1) * quantity)}`
  }
  ${
    item.chance
      ? `${newLine}${item.chanceLabel?.(-1, 0)}: ${formatEng(
          new Decimal(item.chance(-1, 0)).times(100),
          3
        )}% ${
          ri.settings.showCostEffectDetails && item.chanceString
            ? `<span class='subtitle'>${item.chanceString(-1, 0)}</span>`
            : ''
        }`
      : ''
  }</span>`;
};
export const logisticChance: (
  input: DecimalSource,
  steepness: DecimalSource,
  midpoint: DecimalSource
) => Decimal = (input, steepness, midpoint) => {
  input = new Decimal(input);
  steepness = new Decimal(steepness);
  midpoint = new Decimal(midpoint);
  return new Decimal(1).div(
    input.minus(midpoint).times(steepness).times(-1).exp().plus(1)
  );
};
export const formatEng = (num: DecimalSource, places = 4) => {
  const decimal = new Decimal(num);
  const {mantissa, exponent} = decimal;
  // Anything less than 1 should be shown
  if (exponent < 0)
    return decimal.toFixed(places - exponent).replace(/0+$/, '');
  // Less than 100,000, just show in full
  if (exponent < 5) {
    if (mantissa % 1 == 0) {
      return decimal.toFixed(0);
    }
    return decimal.toFixed(Math.max(0, places - exponent));
  }
  return `${(mantissa * Math.pow(10, exponent % 3)).toPrecision(4)}e${
    Math.floor(exponent / 3) * 3
  }`;
};

export const formatPercent: (value: DecimalSource) => string = (value) => {
  return `${formatEng(new Decimal(value).times(100), 1)}%`;
};

export const formatResourceValue = (value: DecimalSource) => formatEng(value);
export const formatResourceCapacity = (capacity: DecimalSource) =>
  formatEng(capacity);

export const formatStat = (stat: DecimalSource) => {
  return formatEng(stat);
};
export const formatStatDelta = (delta: Decimal, places = 4) => {
  let res = '';
  if (delta.eq(0)) return res;
  if (delta.exponent >= -4) {
    res = formatEng(delta, places - Math.min(places, -delta.exponent));
  }
  if (delta.lt(0)) return `-${res.slice(1)}`;
  return `+${res}`;
};

export const formatDelta = (delta: Decimal, places = 4) => {
  if (delta.eq(0)) return '';
  if (delta.lt(0)) return formatEng(delta, places);
  return `+${formatEng(delta, places)}`;
};

export const formatTime = (seconds: DecimalSource, short = false) => {
  if (typeof seconds === 'number' && isNaN(seconds)) return '0s';
  seconds = new Decimal(seconds);
  let result = '';
  const places =
    seconds.lt(10) && new Decimal(seconds.toNumber() % 1).gt(0) ? 2 : 0;
  const minutes = seconds.div(60).floor();
  const hours = minutes.div(60).floor();
  const days = hours.div(24).floor();
  const years = days.div(365).floor();
  let baseString: string;
  if (years.gte(1)) result += `${years.toFixed(0)}${short ? ':' : 'y'}`;
  if (days.gte(1))
    result += `${days.minus(years.times(365)).toFixed(0)}${short ? ':' : 'd'}`;
  if (hours.gte(1))
    result += `${hours.minus(days.times(24)).toFixed(0)}${short ? ':' : 'h'}`;
  if (minutes.gte(1)) {
    baseString = minutes.minus(hours.times(60)).toFixed(0) ?? '';
    result += `${short ? baseString.padStart(2, '0') : baseString}${
      short ? ':' : 'm'
    }`;
  }
  baseString = seconds.minus(minutes.times(60)).toFixed(places);
  return (result += `${short ? baseString.padStart(2, '0') : baseString}${
    short ? '' : 's'
  }`);
};
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const fancy = (name: string, string = '', invert = false) => {
  if (string == '') string = capitalizeFirstLetter(name);
  return `<span class='fancy ${
    invert ? 'invert' : ''
  } ${name}'>${string}</span>`;
};

declare type SummableProperty = 'uses' | 'timeSpent';

export function sumActivitiesByTag(tag: string, prop: SummableProperty) {
  return activitiesByTag(tag).reduce((acc, activity) => {
    if (!Reflect.has(activity, prop)) return acc;
    return acc.add(activity[prop]);
  }, new Decimal(0));
}

export function activitiesByTag(tag: string) {
  return Object.values(ri.activities).filter((activity) =>
    activity.tags.has(tag)
  );
}

export function roundToSteps(input: number, step = 1) {
  return Math.round(input / step) * step;
}

export function assignIfDefined<
  K extends string,
  T extends Partial<Record<K, unknown>>
>(object: {[K in keyof T]: unknown}, overrides: T) {
  for (const [key, value] of typedEntries(overrides)) {
    if (value !== undefined) {
      object[key] = value;
    }
  }
}

// https://stackoverflow.com/questions/13382516/getting-scroll-bar-width-using-javascript
export function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  // outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Removing temporary elements from the DOM
  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
}

export function harmonicNumber(n: DecimalSource): Decimal {
  n = new Decimal(n);
  let res = new Decimal();
  for (let i = new Decimal(0); n.gt(0); n = n.minus(1)) {
    i = i.plus(1);
    res = res.plus(i.reciprocal());
  }
  return res;
}

export function typedEntries<Keys extends PropertyKey, V>(
  object: Partial<Record<Keys, V>>
): [Keys, V][] {
  return Reflect.ownKeys(object).map((k) => [k, object[k]]) as [Keys, V][];
}

export function typedFromEntries<Keys extends PropertyKey, V>(
  entries: [Keys, V][]
): Record<Keys, V> {
  return Object.fromEntries(entries) as Record<Keys, V>;
}

/** Get string/symbol keys with correct types. */
export function typedKeys<Keys extends PropertyKey, V>(
  object: Partial<Record<Keys, V>>
): Keys[] {
  return Reflect.ownKeys(object) as Keys[];
}

// MIT Licensed
// Author: jwilson8767
// Modified: carlank, changed to Observable

/**
 * Waits for an element satisfying selector to exist, then resolves promise with the element.
 * Useful for resolving race conditions.
 */
export function elementReady(
  selector: string,
  rootNode: ShadowRoot | Element | Document = document
): Promise<Element> {
  return new Promise((resolve, _reject) => {
    const el = rootNode.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    new MutationObserver((_mutationRecords, observer) => {
      // Query for elements matching the specified selector
      Array.from(rootNode.querySelectorAll(selector)).forEach((element) => {
        resolve(element);
        //Once we have resolved we don't need the observer anymore.
        observer.disconnect();
      });
    }).observe(
      rootNode instanceof Document ? rootNode.documentElement : rootNode,
      {
        childList: true,
        subtree: true,
      }
    );
  });
}

/** TODO Fix the type issues for 8+ operators......
 *  If you're using this, be careful about types, as it just shoves it through Boolean at the end
 */
export function all$(
  observables: Observable<any>[],
  operator1: OperatorFunction<any, any> = identity,
  operator2: OperatorFunction<any, any> = identity,
  operator3: OperatorFunction<any, any> = identity,
  operator4: OperatorFunction<any, any> = identity,
  operator5: OperatorFunction<any, any> = identity,
  operator6: OperatorFunction<any, any> = identity,
  operator7: OperatorFunction<any, any> = identity
): Observable<boolean> {
  return combineLatest(
    observables.map((o) =>
      o.pipe(
        operator1,
        operator2,
        operator3,
        operator4,
        operator5,
        operator6,
        operator7
      )
    )
  ).pipe(map(all(Boolean)));
}

export function any$(observables: Observable<boolean>[]): Observable<boolean> {
  return combineLatest(observables).pipe(map(any(Boolean)));
}

export function tween(start: number, end: number, duration: number) {
  const diff = end - start;
  return animationFrames().pipe(
    // Figure out what percentage of time has passed
    map(({elapsed}) => elapsed / duration),
    // Take the vector while less than 100%
    takeWhile((v) => v < 1),
    // Finish with 100%
    endWith(1),
    // Calculate the distance traveled between start and end
    map((v) => v * diff + start)
  );
}

/**
 * Returns a random integer at least min and less than max.
 * Arguments do not have to be integral.
 */
export function randInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(min + Math.random() * (max - min));
}

export function randFloat(min: number, max: number) {
  return min + Math.random() * (max - min);
}
export function readColor(image, x, y) {
  const index = (x + y * image.width) * 4;
  return [
    image.pixels[index],
    image.pixels[index + 1],
    image.pixels[index + 2],
    image.pixels[index + 3],
  ];
}

export function writeColor(image, x, y, red, green, blue, alpha) {
  const index = (x + y * image.width) * 4;
  image.pixels[index] = red;
  image.pixels[index + 1] = green;
  image.pixels[index + 2] = blue;
  image.pixels[index + 3] = alpha;
}

export function convertCamelCaseToTitleCase(camelCase: string) {
  return capitalizeFirstLetter(
    camelCase.replace(/([A-Z]+)/g, ' $1').replace(/([A-Z][a-z])/g, ' $1')
  );
}
